/**
 * RipeningPage — fruit ripening (§53). Two halves:
 *  • Predictions — a live, rule-based estimate of each plant's ripe window (computed
 *    server-side from the variety/plant ripe-months profile + this farm's harvest
 *    history; sharpens as harvests are logged). Soonest peak first.
 *  • Observations — log a plant's current stage / colour / firmness / Brix / size,
 *    which refines the prediction's current stage and nudges confidence.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Gauge, Plus, X, Sparkles } from 'lucide-react'
import {
  ripeningApi, plantsApi, RIPENING_STAGES,
  type RipeningStage, type RipeningObservation, type Plant,
} from '../lib/api'

// Stage lifecycle, with a green→amber→red colour ramp (under-ripe → ripe → past).
const STAGE_META: Record<RipeningStage, { label: string; color: string }> = {
  flower_bud:   { label: 'Flower bud',   color: '#65a30d' },
  flowering:    { label: 'Flowering',    color: '#84cc16' },
  fruit_set:    { label: 'Fruit set',    color: '#a3b80a' },
  immature:     { label: 'Immature',     color: '#ca8a04' },
  mature_green: { label: 'Mature green', color: '#d97706' },
  color_break:  { label: 'Color break',  color: '#f59e0b' },
  ripe:         { label: 'Ripe',         color: '#16a34a' },
  overripe:     { label: 'Overripe',     color: '#dc2626' },
  dropped:      { label: 'Dropped',      color: '#6b7280' },
}
const FIRMNESS = ['hard', 'firm', 'soft', 'very_soft']
const stageMeta = (s?: string) => (s && STAGE_META[s as RipeningStage]) || { label: s || '—', color: 'var(--color-text-muted)' }

const card: React.CSSProperties = { background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }

const fmtDate = (s?: string) => s ? new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
const confColor = (pct: number) => pct >= 70 ? '#16a34a' : pct >= 50 ? '#d97706' : 'var(--color-text-muted)'
const daysLabel = (d?: number) => d == null ? '' : d < 0 ? `${-d}d ago` : d === 0 ? 'today' : `in ${d}d`
const sourceLabel = (s: string) => s === 'harvest_history' ? 'your harvest history' : s === 'variety_profile' ? 'variety profile' : s

export function RipeningPage() {
  const [showModal, setShowModal] = useState(false)
  const [presetPlant, setPresetPlant] = useState<string | undefined>(undefined)

  const { data: preds } = useQuery({ queryKey: ['ripening-predictions'], queryFn: () => ripeningApi.predictions().then(r => r.data.predictions) })
  const { data: obs } = useQuery({ queryKey: ['ripening-observations'], queryFn: () => ripeningApi.observations().then(r => r.data.observations) })
  const { data: outlook } = useQuery({ queryKey: ['ripening-outlook'], queryFn: () => ripeningApi.outlook().then(r => r.data), staleTime: 5 * 60_000 })

  const openLog = (plantId?: string) => { setPresetPlant(plantId); setShowModal(true) }

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Gauge size={20} /> Ripening
        </h1>
        <button onClick={() => openLog()} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
          <Plus size={15} /> Log observation
        </button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 18 }}>
        Estimated ripe windows per tree, and a log of what you've seen in the field. Predictions
        sharpen as you log harvests and observations.
      </p>

      {/* AI ripening outlook (auto-generated narrative; falls back to a rule-based summary) */}
      {outlook?.narrative && (
        <div style={{ ...card, marginBottom: 18, borderColor: 'var(--color-primary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Sparkles size={15} style={{ color: 'var(--color-primary)' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>Ripening outlook</span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'color-mix(in srgb, var(--color-primary) 18%, transparent)', color: 'var(--color-primary)' }}>
              {outlook.source === 'vertex' ? 'AUTO' : 'SUMMARY'}
            </span>
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--color-text)', whiteSpace: 'pre-wrap', margin: 0 }}>{outlook.narrative}</p>
          <p style={{ fontSize: 11, color: 'var(--color-text-faint)', margin: '8px 0 0' }}>{outlook.disclaimer}</p>
        </div>
      )}

      {/* Predictions */}
      <div style={{ ...card, marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 12 }}>Predicted ripe windows</div>
        {!preds?.length ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
            No predictions yet. Set <strong>ripe months</strong> on your varieties, or log a harvest, and
            estimated windows will appear here.
          </p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {preds.map(p => {
              const sm = stageMeta(p.current_stage)
              return (
                <div key={p.plant_id} style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12, background: 'var(--color-bg)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.plant_label}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>{p.variety_name}</div>
                    </div>
                    {p.days_until_peak != null && (
                      <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: confColor(p.confidence_pct) }}>{daysLabel(p.days_until_peak)}</span>
                    )}
                  </div>

                  <div style={{ fontSize: 12.5, color: 'var(--color-text)', marginBottom: 6 }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Peak </span>
                    <strong>{fmtDate(p.predicted_peak)}</strong>
                    <span style={{ color: 'var(--color-text-faint)' }}> ({fmtDate(p.predicted_start)} – {fmtDate(p.predicted_end)})</span>
                  </div>

                  {p.current_stage && (
                    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, marginBottom: 8, color: sm.color, background: `color-mix(in srgb, ${sm.color} 16%, transparent)` }}>
                      {sm.label}{p.last_observed_at ? ` · seen ${fmtDate(p.last_observed_at)}` : ''}
                    </span>
                  )}

                  {/* Confidence */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 3 }}>
                    <span>Confidence</span><span style={{ color: confColor(p.confidence_pct), fontWeight: 600 }}>{p.confidence_pct}%</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: 'var(--color-bg-card)', overflow: 'hidden', marginBottom: 8 }}>
                    <div style={{ width: `${Math.min(100, p.confidence_pct)}%`, height: '100%', background: confColor(p.confidence_pct) }} />
                  </div>

                  <p style={{ fontSize: 11, color: 'var(--color-text-faint)', margin: '0 0 8px' }}>
                    {p.reasoning} <span style={{ opacity: 0.8 }}>(source: {sourceLabel(p.source)})</span>
                  </p>

                  <button onClick={() => openLog(p.plant_id)} style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    + Log observation
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Recent observations */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10 }}>Recent observations</div>
        {!obs?.length ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>No observations logged yet. Click "Log observation" to record a tree's current stage.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {obs.map(o => {
              const m = stageMeta(o.stage)
              const detail = [
                o.color_description,
                o.firmness,
                o.brix != null ? `${o.brix}°Bx` : '',
                o.size_cm != null ? `${o.size_cm} cm` : '',
                o.notes,
              ].filter(Boolean).join(' · ')
              return (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--color-bg)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 600 }}>
                      {m.label}{o.plant_label ? ` · ${o.plant_label}` : ''}{o.variety_name ? ` · ${o.variety_name}` : ''}
                    </div>
                    {detail && <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</div>}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', flexShrink: 0 }}>{fmtDate(o.observed_at)}</div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {showModal && <LogModal presetPlant={presetPlant} onClose={() => setShowModal(false)} />}
    </main>
  )
}

function LogModal({ presetPlant, onClose }: { presetPlant?: string; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: plants } = useQuery({ queryKey: ['plants'], queryFn: () => plantsApi.list().then(r => r.data.plants) })
  const [f, setF] = useState({
    plant_id: presetPlant ?? '',
    stage: 'mature_green' as RipeningStage,
    color_description: '', firmness: '', brix: '', size_cm: '', notes: '',
    observed_at: new Date().toISOString().slice(0, 10),
  })
  const set = (k: keyof typeof f, v: string) => setF(p => ({ ...p, [k]: v }))

  const save = useMutation({
    mutationFn: () => ripeningApi.logObservation({
      plant_id: f.plant_id,
      stage: f.stage,
      color_description: f.color_description || undefined,
      firmness: f.firmness || undefined,
      brix: f.brix ? parseFloat(f.brix) : undefined,
      size_cm: f.size_cm ? parseFloat(f.size_cm) : undefined,
      notes: f.notes || undefined,
      observed_at: f.observed_at ? new Date(f.observed_at + 'T12:00:00').toISOString() : undefined,
    } as Partial<RipeningObservation>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ripening-observations'] })
      qc.invalidateQueries({ queryKey: ['ripening-predictions'] })
      onClose()
    },
  })

  const plantLabel = (p: Plant) => `${p.label || 'Tree'}${p.variety?.name ? ` · ${p.variety.name}` : ''}`

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: 460, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>Log ripening observation</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={lbl}>Tree / plant</label>
            <select style={inp} value={f.plant_id} onChange={e => set('plant_id', e.target.value)}>
              <option value="">— select a tree —</option>
              {plants?.map(p => <option key={p.id} value={p.id}>{plantLabel(p)}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Stage</label>
            <select style={inp} value={f.stage} onChange={e => set('stage', e.target.value)}>
              {RIPENING_STAGES.map(s => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Firmness</label>
              <select style={inp} value={f.firmness} onChange={e => set('firmness', e.target.value)}>
                <option value="">—</option>
                {FIRMNESS.map(x => <option key={x} value={x}>{x.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Brix (°Bx)</label><input style={inp} type="number" min={0} step={0.1} value={f.brix} onChange={e => set('brix', e.target.value)} placeholder="refractometer" /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Size (cm)</label><input style={inp} type="number" min={0} step={0.1} value={f.size_cm} onChange={e => set('size_cm', e.target.value)} placeholder="approx diameter" /></div>
            <div><label style={lbl}>Date</label><input style={inp} type="date" value={f.observed_at} onChange={e => set('observed_at', e.target.value)} /></div>
          </div>
          <div><label style={lbl}>Colour</label><input style={inp} value={f.color_description} onChange={e => set('color_description', e.target.value)} placeholder='e.g. "dark green, blushing red shoulder"' /></div>
          <div><label style={lbl}>Notes</label><input style={inp} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="optional" /></div>
          {save.isError && <p style={{ fontSize: 12, color: '#dc2626' }}>Could not save — try again.</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-muted)' }}>Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending || !f.plant_id} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: save.isPending || !f.plant_id ? 'not-allowed' : 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, opacity: !f.plant_id ? 0.5 : 1 }}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RipeningPage
